import { useEffect, useRef } from 'react';

import { matchesNew } from '@components/bid_request/details/utils';
import { useLotPageQuery } from '@features/buyer-line-items-pagination/api/queries';
import type { LotPageLoad } from '@features/buyer-line-items-pagination/types/lot-page';
import type { ID } from 'types';

interface UseLotPageParams {
  eventId: ID;
  loads: number;
  lotId: ID;
  onPageLoaded: (load: LotPageLoad) => void;
  page: number;
  pageSize: number;
  shellRefreshCount: number;
}

/**
 * Owns every page read of one lot, page 1 included. After each successful read of the current key it reports
 * the page through `onPageLoaded`, even when the rows are identical, with the refresh count the read was sent
 * under. A key change that hits the cache reports the cached page at once with no refresh marker, then again
 * when the background check lands. An unsaved `new-` lot reads nothing and counts as loaded at once.
 */
export const useLotPage = ({ eventId, loads, lotId, onPageLoaded, page, pageSize, shellRefreshCount }: UseLotPageParams) => {
  const refreshCount = useRef(shellRefreshCount);
  refreshCount.current = shellRefreshCount;
  const lastReport = useRef<string>();
  const isNewLot = matchesNew(String(lotId));
  const query = useLotPageQuery({
    enabled: !isNewLot,
    eventId,
    getRefreshMarker: () => refreshCount.current,
    lotId,
    page,
    pageSize,
    search: '',
  });
  const { data, dataUpdatedAt, isError, isFetching, refetch } = query;

  useEffect(() => {
    const report = `${lotId}|${page}|${pageSize}|${dataUpdatedAt}`;
    if (!data || lastReport.current === report) return;

    lastReport.current = report;
    onPageLoaded({
      lotId,
      page,
      pageIds: data.records.map(record => record.id),
      pageSize,
      refreshMarker: isFetching ? undefined : data.refreshMarker,
      rows: data.records,
      total: data.pagination.total,
    });
  }, [data, dataUpdatedAt, isFetching, lotId, onPageLoaded, page, pageSize]);

  return {
    hasLoadedPage: isNewLot || loads > 0,
    isFetchingPage: isFetching,
    isPageError: isError,
    retry: refetch,
  };
};
